import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import type { UserListParams, CreateUserRequest, UpdateUserRequest } from '@conai/shared-types';
import { useUsersService } from '../providers/ApiProvider';
import { queryKeys } from '../config/query-client';
import { api } from '../config/api';
import { useI18n } from '../i18n';

export function useUsers(params: UserListParams = {}) {
  const usersService = useUsersService();

  return useQuery({
    queryKey: queryKeys.users.list(params as Record<string, unknown>),
    queryFn: () => usersService.list(params),
  });
}

/**
 * Workforce stats (GET /users/stats/): active/on_leave counts, by_role breakdown,
 * and month-over-month delta. Excludes CUSTOMER role (server-side).
 */
export function useUsersStats() {
  const { data, isLoading } = useQuery({
    queryKey: ['users', 'stats'] as const,
    queryFn: () => api.client.get<Record<string, unknown>>('/users/stats/'),
    staleTime: 60 * 1000,
  });

  const d = ((data as any)?.data ?? data ?? {}) as Record<string, any>;
  const stats = {
    totalStaff: Number(d.total_staff ?? 0),
    active: Number(d.active ?? 0),
    onLeave: Number(d.on_leave ?? 0),
    byRole: (d.by_role ?? {}) as Record<string, number>,
    deltaPercent: d.delta_percent == null ? null : Number(d.delta_percent),
  };
  return { stats, isLoading };
}

export function useCreateUser() {
  const usersService = useUsersService();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    mutationFn: (data: CreateUserRequest) => usersService.create(data),
    onSuccess: (newUser) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });
      toast.success(t('toastEmployeeCreated', { name: newUser.full_name }));
    },
  });
}

/**
 * Đặt lại mật khẩu nhân viên (sheet 2 #3/#63) — POST /users/{id}/reset-password/.
 *
 * KHÔNG toast ở đây: mật khẩu server sinh chỉ trả về ĐÚNG MỘT LẦN, nên trang
 * phải hiện nó trong hộp thoại cho người dùng chép lại, không thể nhét vào một
 * toast tự biến mất sau 4 giây.
 */
export function useResetUserPassword() {
  const usersService = useUsersService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, newPassword }: { id: string; newPassword?: string }) =>
      usersService.resetPassword(id, newPassword),
    onSuccess: () => {
      // `must_change_password` đổi -> danh sách nhân viên phải đọc lại
      queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });
    },
  });
}

/**
 * Tắt 2FA hộ một nhân viên khác — chỉ AI_ADMIN (sheet 2 mục #3).
 *
 * BE cố ý KHÔNG phát recovery code, nên đây là đường DUY NHẤT khi nhân viên
 * mất điện thoại. Giao diện phải nói rõ hệ quả trước khi bấm: tài khoản đó
 * quay về chỉ còn mật khẩu bảo vệ.
 */
export function useDisableUserTwoFactor() {
  const usersService = useUsersService();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    mutationFn: (id: string) => usersService.disableTwoFactorFor(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });
      toast.success(t('twoFactorDisabledForUser'));
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : t('twoFactorDisableFailed'));
    },
  });
}

export function useUpdateUser() {
  const usersService = useUsersService();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserRequest }) =>
      usersService.update(id, data),
    onSuccess: (updatedUser) => {
      queryClient.setQueryData(queryKeys.users.detail(updatedUser.id), updatedUser);
      queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });
      toast.success(t('toastEmployeeUpdated', { name: updatedUser.full_name }));
    },
  });
}

export function useDeleteUser() {
  const usersService = useUsersService();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    mutationFn: (id: string) => usersService.delete(id),
    onSuccess: (_, deletedId) => {
      queryClient.removeQueries({ queryKey: queryKeys.users.detail(deletedId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });
      toast.success(t('toastEmployeeDeleted'));
    },
  });
}

export function useDeactivateUser() {
  const usersService = useUsersService();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    mutationFn: (id: string) => usersService.deactivate(id),
    onSuccess: (updatedUser) => {
      queryClient.setQueryData(queryKeys.users.detail(updatedUser.id), updatedUser);
      queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });
      toast.success(t('toastEmployeeDeactivated', { name: updatedUser.full_name }));
    },
    // Bug #68 (reviewer bắt): modal giờ BẮT người dùng chờ ("yêu cầu đã gửi,
    // hãy chờ kết quả") — bắt chờ thì phải báo kết quả, kể cả kết quả xấu.
    // Không có onError, request hỏng làm modal đóng im lặng y hệt lúc thành
    // công, người dùng tưởng đã deactivate trong khi chưa.
    onError: (error) => {
      toast.error(error instanceof Error && error.message ? error.message : t('failedToDeactivateEmployee'));
    },
  });
}

export function useActivateUser() {
  const usersService = useUsersService();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    mutationFn: (id: string) => usersService.activate(id),
    onSuccess: (updatedUser) => {
      queryClient.setQueryData(queryKeys.users.detail(updatedUser.id), updatedUser);
      queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });
      toast.success(t('toastEmployeeActivated', { name: updatedUser.full_name }));
    },
    onError: (error) => {
      toast.error(error instanceof Error && error.message ? error.message : t('failedToActivateEmployee'));
    },
  });
}
